import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaTrashAlt, FaFilter } from "react-icons/fa";
import { FiRotateCcw } from "react-icons/fi";
import Select from "react-select";

import { useGetAllLabelledStockQuery } from "../toolkitServices/stockApi";

// masters
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";
import { useGetAllVendorsQuery } from "../toolkitServices/vendorApi";

import { useDispatch, useSelector } from "react-redux";
import { setLabelledList } from "../slices/labelledStockSlice";
import { setCategoryList } from "../slices/categorySlice";
import { setProductList } from "../slices/productSlice";
import { setVendorList } from "../slices/vendorSlice";
import { setDesignList } from "../slices/designSlice";
import { setPurityList } from "../slices/puritySlice";
import { setBranchList } from "../slices/branchSlice";

import { handleDropDowns } from "../config/dropDownHelpers";
import { formatDate } from "../config/helperFunctions";

const toTitle = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);
const STATUS_MAP = [
  { label: "All Status", value: "" },
  { label: "Active", value: "active" },
  { label: "Sold", value: "sold" },
  { label: "Draft", value: "draft" },
  { label: "Inactive", value: "inactive" },
];

// react-select styles
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    borderColor: state.isFocused ? "#9481ce" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #9481ce" : "none",
    "&:hover": { borderColor: "#b2a6e6" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const PurchaseList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // header controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // ✅ applied vs draft filters
  const emptyFilter = {
    vendorId: "",
    branchId: "",
    categoryId: "",
    productId: "",
    designId: "",
    purityId: "",
    dateFrom: "",
    dateTo: "",
  };
  const [appliedFilters, setAppliedFilters] = useState(emptyFilter); // used in query
  const [draftFilters, setDraftFilters] = useState(emptyFilter); // used in modal
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // masters
  const { data: vendors } = useGetAllVendorsQuery();
  const { data: branches } = useGetAllBranchesQuery();
  const { data: categories } = useGetAllCategoriesQuery();
  const { data: products } = useGetAllProductsQuery();
  const { data: designs } = useGetAllDesignsQuery();
  const { data: purities } = useGetAllPuritiesQuery();

  // push masters to store
  useEffect(() => {
    if (categories?.data?.length) dispatch(setCategoryList(categories.data));
    if (products?.data?.length) dispatch(setProductList(products.data));
    if (vendors?.data?.length) dispatch(setVendorList(vendors.data));
    if (designs?.data?.length) dispatch(setDesignList(designs.data));
    if (purities?.data?.length) dispatch(setPurityList(purities.data));
    if (branches?.data?.length) dispatch(setBranchList(branches.data));
  }, [dispatch, categories, products, vendors, designs, purities, branches]);

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const vendorsFromState = useSelector((s) => s.vendors?.vendors);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);

  // server query with pagination + filters (✅ uses appliedFilters only)
  const { data, isFetching, isSuccess } = useGetAllLabelledStockQuery({
    page,
    limit,
    search,
    status: statusFilter,
    ...appliedFilters, // NOT draft
  });

  const labelledStocksInStore = useSelector(
    (state) => state?.labelledStocks?.labelledStock
  );

  // keep current page data
  useEffect(() => {
    if (isSuccess && Array.isArray(data?.data)) {
      dispatch(setLabelledList(data.data));
    }
  }, [isSuccess, data, dispatch]);

  // keyboard shortcut (Ctrl+P)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/purchase/purchase-stock-list/create-purchase");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  // debounced search resets to page 1
  useEffect(() => {
    const t = setTimeout(() => setPage(1), 350);
    return () => clearTimeout(t);
  }, [search, statusFilter, limit]);

  const rows = labelledStocksInStore || [];
  const totalDocs = data?.pagination?.totalDocs || 0;
  const totalPages = data?.pagination?.totalPages || 1;
  const hasPrev = data?.pagination?.hasPrev || page > 1;
  const hasNext = data?.pagination?.hasNext || page < totalPages;

  // ----- react-select options with your helper -----
  const findOpt = (opts, v) =>
    (opts || []).find((o) => String(o.value) === String(v)) || null;

  // Non-dependent lists
  const vendorOptions = useMemo(
    () => handleDropDowns({ key: "vendor", vendors: vendorsFromState }),
    [vendorsFromState]
  );
  const branchOptions = useMemo(
    () => handleDropDowns({ key: "branch", branches: branchesFromState }),
    [branchesFromState]
  );
  const categoryOptions = useMemo(
    () => handleDropDowns({ key: "category", categories: categoriesFromState }),
    [categoriesFromState]
  );

  // values for modal (draft)
  const vendorValue = useMemo(
    () => findOpt(vendorOptions, draftFilters.vendorId),
    [vendorOptions, draftFilters.vendorId]
  );
  const branchValue = useMemo(
    () => findOpt(branchOptions, draftFilters.branchId),
    [branchOptions, draftFilters.branchId]
  );
  const categoryValue = useMemo(
    () => findOpt(categoryOptions, draftFilters.categoryId),
    [categoryOptions, draftFilters.categoryId]
  );

  // dependent options (use draft selected)
  const productOptions = useMemo(
    () =>
      handleDropDowns({
        key: "product",
        products: productsFromState,
        category: categoryValue, // selected object
      }),
    [productsFromState, categoryValue]
  );
  const productValue = useMemo(
    () => findOpt(productOptions, draftFilters.productId),
    [productOptions, draftFilters.productId]
  );

  const designOptions = useMemo(
    () =>
      handleDropDowns({
        key: "design",
        designs: designFromState,
        product: productValue, // selected object
      }),
    [designFromState, productValue]
  );
  const designValue = useMemo(
    () => findOpt(designOptions, draftFilters.designId),
    [designOptions, draftFilters.designId]
  );

  const purityOptions = useMemo(
    () =>
      handleDropDowns({
        key: "purity",
        purities: purityFromState,
        category: categoryValue, // selected object
      }),
    [purityFromState, categoryValue]
  );
  const purityValue = useMemo(
    () => findOpt(purityOptions, draftFilters.purityId),
    [purityOptions, draftFilters.purityId]
  );

  // ----- handlers -----
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters((s) => ({ ...s, [name]: value }));
  };

  const onVendorChange = (opt) =>
    setDraftFilters((s) => ({ ...s, vendorId: opt?.value || "" }));

  const onBranchChange = (opt) =>
    setDraftFilters((s) => ({ ...s, branchId: opt?.value || "" }));

  const onCategoryChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      categoryId: opt?.value || "",
      productId: "",
      designId: "",
      purityId: "",
    }));

  const onProductChange = (opt) =>
    setDraftFilters((s) => ({
      ...s,
      productId: opt?.value || "",
      designId: "",
      purityId: "",
    }));

  const onDesignChange = (opt) =>
    setDraftFilters((s) => ({ ...s, designId: opt?.value || "" }));

  const onPurityChange = (opt) =>
    setDraftFilters((s) => ({ ...s, purityId: opt?.value || "" }));

  // open modal -> copy applied -> draft
  const openFilters = () => {
    setDraftFilters(appliedFilters);
    setIsFilterOpen(true);
  };

  // Apply -> copy draft -> applied (query runs), reset page
  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    setPage(1);
    setIsFilterOpen(false);
  };

  // Reset (draft only)
  const resetDraftFilters = () => setDraftFilters(emptyFilter);

  return (
    <div className="bg-white shadow-md rounded-xl">
      {/* Header and Filters */}
      <div className="flex flex-wrap justify-between items-center px-4 py-3 bg-[#9481ce] rounded-t-xl text-white">
        <h2 className="text-lg font-semibold">Purchased Stock List</h2>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search (code, product, category, etc.)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1 rounded-md text-sm text-gray-800 focus:outline-none"
          />

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1 rounded-md text-sm text-gray-800"
          >
            {STATUS_MAP.map((s) => (
              <option key={s.value || "all"} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1 rounded-md text-sm text-gray-800"
            title="Rows per page"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>

          <button
            onClick={openFilters}
            className="bg-white/20 border border-white/30 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-white/30 flex items-center gap-2"
            title="Filters"
          >
            <FaFilter /> Filters
          </button>

          <Link
            to={"/purchase/purchase-stock-list/create-purchase"}
            className="bg-white text-[#9481ce] px-4 py-1.5 rounded-md text-sm font-medium hover:bg-violet-100"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto max-h-[calc(100vh-260px)] overflow-y-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-[#e6e1f5] text-[#4b3d91] uppercase text-xs sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-center">Item Code</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Design</th>
              <th className="px-4 py-3">Purity</th>
              <th className="px-4 py-3">Total Wt</th>
              <th className="px-4 py-3">Net Wt</th>
              <th className="px-4 py-3">Branch</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created On</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence>
              {isFetching && (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td
                    colSpan={11}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    Loading…
                  </td>
                </motion.tr>
              )}

              {!isFetching && rows?.length > 0
                ? rows.map((item, index) => (
                    <motion.tr
                      key={item._id || `${item.itemCode}-${index}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25, delay: index * 0.03 }}
                      className="border-t hover:bg-[#f4f0ff] transition"
                    >
                      <td className="px-4 py-2 font-medium">
                        <div className="inline-block px-3 py-1 rounded-full border text-center">
                          {item.itemCode}
                        </div>
                      </td>
                      <td className="px-4 py-2">{item.productName}</td>
                      <td className="px-4 py-2">{item.categoryName}</td>
                      <td className="px-4 py-2">{item.designName}</td>
                      <td className="px-4 py-2">{item.purityName}</td>
                      <td className="px-4 py-2">{item.totalWt}</td>
                      <td className="px-4 py-2">{item.netWt}</td>
                      <td className="px-4 py-2">{item.branchName}</td>
                      <td className="px-4 py-2">
                        <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs">
                          {toTitle(item.status || "")}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-[#9481ce] text-sm p-2 rounded-full hover:bg-[#9481ce]/20"
                          title="Edit"
                        >
                          <FaPencilAlt />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="text-[#9481ce] text-sm p-2 rounded-full hover:bg-[#9481ce]/20"
                          title="Delete"
                        >
                          <FaTrashAlt />
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))
                : !isFetching && (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td
                        colSpan={11}
                        className="text-center text-gray-500 py-6"
                      >
                        No matching records found.
                      </td>
                    </motion.tr>
                  )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
        <div className="text-sm text-gray-600">
          Page <b>{page}</b> / {totalPages} • Total: <b>{totalDocs}</b>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
            disabled={!hasPrev || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </button>
          <button
            className="px-3 py-1.5 rounded border hover:bg-gray-100 disabled:opacity-50"
            disabled={!hasNext || isFetching}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* ✅ Filter Modal (Apply-only) */}
      {isFilterOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <motion.div
            className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-3xl"
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button
                onClick={resetDraftFilters}
                className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border hover:bg-gray-50"
                title="Reset all"
              >
                <FiRotateCcw /> Reset
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Vendor */}
              <div>
                <label className="text-xs text-gray-500">Vendor</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={vendorOptions}
                  value={vendorValue}
                  onChange={onVendorChange}
                  placeholder="Select vendor…"
                />
              </div>

              {/* Branch */}
              <div>
                <label className="text-xs text-gray-500">Branch</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={branchOptions}
                  value={branchValue}
                  onChange={onBranchChange}
                  placeholder="Select branch…"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-xs text-gray-500">Category</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={categoryOptions}
                  value={categoryValue}
                  onChange={onCategoryChange}
                  placeholder="Select category…"
                />
              </div>

              {/* Product (depends on Category) */}
              <div>
                <label className="text-xs text-gray-500">Product</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={productOptions}
                  value={productValue}
                  onChange={onProductChange}
                  placeholder="Select product…"
                  isDisabled={!draftFilters.categoryId}
                />
              </div>

              {/* Design (depends on Product) */}
              <div>
                <label className="text-xs text-gray-500">Design</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={designOptions}
                  value={designValue}
                  onChange={onDesignChange}
                  placeholder="Select design…"
                  isDisabled={!draftFilters.productId}
                />
              </div>

              {/* Purity (filtered by Category) */}
              <div>
                <label className="text-xs text-gray-500">Purity</label>
                <Select
                  isClearable
                  styles={selectStyles}
                  options={purityOptions}
                  value={purityValue}
                  onChange={onPurityChange}
                  placeholder="Select purity…"
                />
              </div>

              {/* Dates */}
              <div>
                <label className="text-xs text-gray-500">From</label>
                <input
                  type="date"
                  name="dateFrom"
                  value={draftFilters.dateFrom}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 rounded-md border"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">To</label>
                <input
                  type="date"
                  name="dateTo"
                  value={draftFilters.dateTo}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 rounded-md border"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setIsFilterOpen(false)}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 rounded-md bg-[#9481ce] text-white"
              >
                Apply & Run
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PurchaseList;
